import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { Plus, Trash, Truck, Phone, Pencil, Check } from '@phosphor-icons/react';

export default function ChauffeursPanel() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ naam: '', tel: '', nummerplaat: '' });
  // Per-rij save-status — toont een ✓-bevestiging na succesvolle update
  // Sleutel = chauffeur-id, waarde = 'saving' | 'saved' | null
  const [savingState, setSavingState] = useState({});

  const load = () => {
    API.get('/chauffeurs').then(r => setItems(r.data || [])).catch(() => setItems([]));
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.naam.trim()) return;
    setBusy(true);
    try {
      await API.post('/chauffeurs', form);
      setForm({ naam: '', tel: '', nummerplaat: '' });
      load();
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Chauffeur verwijderen?')) return;
    await API.delete(`/chauffeurs/${id}`);
    load();
  };

  // Update + visuele feedback ("Opgeslagen!" badge gedurende 1,5s)
  const update = async (id, patch) => {
    setSavingState(p => ({ ...p, [id]: 'saving' }));
    try {
      await API.put(`/chauffeurs/${id}`, patch);
      setSavingState(p => ({ ...p, [id]: 'saved' }));
      setTimeout(() => setSavingState(p => ({ ...p, [id]: null })), 1800);
      // Stille lokale update zodat de input zijn defaultValue behoudt (geen jitter)
      setItems(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c));
    } catch {
      setSavingState(p => ({ ...p, [id]: null }));
      alert('Wijziging niet opgeslagen — probeer opnieuw.');
    }
  };

  return (
    <div className="space-y-6" data-testid="chauffeurs-panel">
      <div>
        <h2 className="text-xl font-bold text-[#0A0A0A] tracking-tight">Chauffeurs</h2>
        <p className="text-sm text-gray-500 mt-1">
          Beheer de chauffeurs die leveringen uitvoeren. <strong className="text-[#002FA7]">Tip: klik in een veld in de tabel hieronder om de chauffeur te wijzigen</strong> — wijzigingen worden automatisch opgeslagen wanneer je het veld verlaat (Tab of klik buiten).
        </p>
      </div>

      {/* Nieuwe chauffeur */}
      <div className="bg-white border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
          <Truck size={16} className="text-[#002FA7]" />
          <span className="text-xs font-bold uppercase tracking-wider text-gray-700">Nieuwe chauffeur toevoegen</span>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            className="field-input md:col-span-2"
            placeholder="Naam (verplicht)"
            value={form.naam}
            onChange={e => setForm(s => ({ ...s, naam: e.target.value }))}
            data-testid="ch-new-naam"
          />
          <input
            className="field-input"
            placeholder="Tel"
            value={form.tel}
            onChange={e => setForm(s => ({ ...s, tel: e.target.value }))}
            data-testid="ch-new-tel"
          />
          <input
            className="field-input"
            placeholder="Nummerplaat"
            value={form.nummerplaat}
            onChange={e => setForm(s => ({ ...s, nummerplaat: e.target.value }))}
            data-testid="ch-new-plaat"
          />
          <div className="md:col-span-4 flex justify-end">
            <button
              className="btn btn-primary btn-sm inline-flex items-center gap-1.5"
              onClick={add}
              disabled={busy || !form.naam.trim()}
              data-testid="ch-new-submit"
            >
              <Plus size={14} /> Toevoegen
            </button>
          </div>
        </div>
      </div>

      {/* Lijst */}
      <div className="bg-white border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-wider text-gray-700">Actieve chauffeurs</span>
          <span className="text-xs text-gray-400">{items.length}</span>
        </div>
        {items.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-400">Nog geen chauffeurs toegevoegd.</div>
        ) : (
          <table className="w-full text-sm" data-testid="ch-list">
            <thead className="text-[11px] uppercase tracking-wider text-gray-500 bg-gray-50/50">
              <tr>
                <th className="text-left px-4 py-2 font-bold">
                  <span className="inline-flex items-center gap-1.5"><Pencil size={11} weight="bold" className="text-[#002FA7]" /> Naam</span>
                </th>
                <th className="text-left px-4 py-2 font-bold">
                  <span className="inline-flex items-center gap-1.5"><Pencil size={11} weight="bold" className="text-[#002FA7]" /> Telefoon</span>
                </th>
                <th className="text-left px-4 py-2 font-bold">
                  <span className="inline-flex items-center gap-1.5"><Pencil size={11} weight="bold" className="text-[#002FA7]" /> Nummerplaat</span>
                </th>
                <th className="text-center px-4 py-2 font-bold w-20">Actief</th>
                <th className="w-32 text-center px-2 py-2 font-bold">Status</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(c => {
                const status = savingState[c.id];
                return (
                <tr key={c.id} className="border-t border-gray-100 hover:bg-blue-50/30 transition-colors group" data-testid={`ch-row-${c.id}`}>
                  <td className="px-4 py-2.5 font-medium text-gray-900">
                    <EditableInput
                      defaultValue={c.naam}
                      onSave={(v) => v !== c.naam && update(c.id, { naam: v })}
                      placeholder="Naam"
                      data-testid={`ch-naam-${c.id}`}
                    />
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <Phone size={12} className="text-gray-400 shrink-0" />
                      <EditableInput
                        defaultValue={c.tel || ''}
                        onSave={(v) => v !== (c.tel || '') && update(c.id, { tel: v })}
                        placeholder="Telefoon"
                        data-testid={`ch-tel-${c.id}`}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <EditableInput
                      defaultValue={c.nummerplaat || ''}
                      onSave={(v) => v.toUpperCase() !== (c.nummerplaat || '') && update(c.id, { nummerplaat: v.toUpperCase() })}
                      placeholder="1-ABC-123"
                      uppercase
                      data-testid={`ch-plaat-${c.id}`}
                    />
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <label className="inline-flex items-center cursor-pointer" title="Klik om chauffeur (de)activeren">
                      <input
                        type="checkbox"
                        className="w-4 h-4 cursor-pointer accent-[#002FA7]"
                        checked={c.actief !== false}
                        onChange={e => update(c.id, { actief: e.target.checked })}
                        data-testid={`ch-actief-${c.id}`}
                      />
                    </label>
                  </td>
                  <td className="px-2 py-2.5 text-center">
                    {status === 'saving' && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-gray-500 italic" data-testid={`ch-status-saving-${c.id}`}>
                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-pulse"></span>
                        Opslaan...
                      </span>
                    )}
                    {status === 'saved' && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-green-700 bg-green-100 border border-green-300 rounded-full px-2 py-0.5 animate-pulse" data-testid={`ch-status-saved-${c.id}`}>
                        <Check size={10} weight="bold" /> Opgeslagen
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2.5 text-right">
                    <button
                      className="text-gray-300 hover:text-[#E02424] p-1.5 hover:bg-red-50 rounded transition-colors"
                      onClick={() => remove(c.id)}
                      title="Chauffeur verwijderen"
                      data-testid={`ch-del-${c.id}`}
                    >
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/**
 * Bewerkbaar tekst-invoerveld met duidelijke visuele indicatie dat het editable is.
 * Toont een lichte border + hover-effect zodat de gebruiker direct ziet dat hij kan klikken.
 * Slaat op via onSave bij blur (verlies van focus) of Enter.
 */
function EditableInput({ defaultValue, onSave, placeholder, uppercase, ...rest }) {
  return (
    <input
      type="text"
      className={`w-full bg-white border border-gray-200 hover:border-[#002FA7] focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 rounded px-2 py-1 outline-none transition-all text-sm ${uppercase ? 'uppercase font-mono' : ''}`}
      defaultValue={defaultValue}
      placeholder={placeholder}
      onBlur={e => onSave(e.target.value.trim())}
      onKeyDown={e => {
        if (e.key === 'Enter') { e.target.blur(); }
        if (e.key === 'Escape') { e.target.value = defaultValue; e.target.blur(); }
      }}
      {...rest}
    />
  );
}
